import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import { task } from 'ember-concurrency';
import SlideToggle from 'garaje/components/slide-toggle';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type Config from 'garaje/models/config';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface Signature {
  Args: {
    config: Config;
    onUpgrade?: () => void;
  };
}

export default class SecurityFullLegalNameComponent extends Component<Signature> {
  @service declare abilities: AbilitiesService;
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;

  toggleTask = task({ drop: true }, async () => {
    this.args.config.legalNameValidationEnabled = !this.args.config.legalNameValidationEnabled;

    try {
      await this.args.config.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      this.args.config.rollbackAttributes();
    }
  });

  get isDisabled(): boolean {
    return this.toggleTask.isRunning || !this.abilities.can('update full legal name setting in security');
  }

  get hasAccessToLegalNameValidationFeature(): boolean {
    return !!this.state.features?.canAccessFullLegalName;
  }

  @action
  onUpgradeButtonClicked(event: PointerEvent): void {
    event.preventDefault();
    this.args.onUpgrade?.();
  }

  <template>
    <SettingsPanel as |panel|>
      <panel.header
        @iconUri="/assets/images/security/person.svg"
        @title="Ask visitor to provide their full legal name"
        @description="Visitors will need to provide their first, middle, and last name."
        as |header|
      >
        <header.controls>
          {{#if this.hasAccessToLegalNameValidationFeature}}
            <SlideToggle
              data-test-full-legal-name-toggle
              @isDisabled={{this.isDisabled}}
              @isChecked={{@config.legalNameValidationEnabled}}
              @onClick={{this.toggleTask.perform}}
            />
          {{else}}
            <button
              data-test-full-legal-name-upgrade-button
              type="button"
              class="btn btn--small btn--brand"
              {{on "click" this.onUpgradeButtonClicked}}
            >
              Upgrade
            </button>
          {{/if}}
        </header.controls>
      </panel.header>
    </SettingsPanel>
  </template>
}
